/**
 * Future Closure Configuration Panel
 * Admin CRUD for organization-level multipliers, defaults, and continuation rates.
 * Used inside the Settings page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Settings2, Save, RotateCcw, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import {
  getFutureClosureConfig,
  updateFutureClosureConfig,
} from '../services/futureClosureApi';

interface ConfigState {
  aggression_default: number;
  base_months_default: number;
  closure_duration_months: number;
  custom_continuations: {
    officer_salary: number;
    employee_wages: number;
    rent: number;
    utilities: number;
    insurance: number;
    other_expenses: number;
  };
  custom_one_time_costs: {
    shutdown_pct: number;
    restart_pct: number;
  };
  multiplier_mode: string;
}

const DEFAULT_CONFIG: ConfigState = {
  aggression_default: 50,
  base_months_default: 36,
  closure_duration_months: 36,
  custom_continuations: {
    officer_salary: 100,
    employee_wages: 100,
    rent: 100,
    utilities: 30,
    insurance: 100,
    other_expenses: 35,
  },
  custom_one_time_costs: {
    shutdown_pct: 5,
    restart_pct: 10,
  },
  multiplier_mode: 'profits_only',
};

const CONTINUATION_LABELS: Record<string, { label: string; description: string }> = {
  officer_salary: { label: 'Officer Salary', description: 'Owner/officer compensation during closure' },
  employee_wages: { label: 'Employee Wages', description: 'Staff payroll during wind-down period' },
  rent: { label: 'Rent / Lease', description: 'Fixed lease obligations that continue' },
  utilities: { label: 'Utilities', description: 'Reduced utility costs during closure' },
  insurance: { label: 'Insurance', description: 'Insurance premiums that must be maintained' },
  other_expenses: { label: 'Other Expenses', description: 'Miscellaneous ongoing costs' },
};

const MULTIPLIER_MODES = [
  { value: 'profits_only', label: 'Profits Only', description: 'Apply multiplier only to lost profits' },
  { value: 'full_loss', label: 'Full Loss', description: 'Apply multiplier to total loss including costs' },
  { value: 'none', label: 'None', description: 'No multiplier applied' },
];

export default function FutureClosureConfigPanel() {
  const [config, setConfig] = useState<ConfigState>(DEFAULT_CONFIG);
  const [originalConfig, setOriginalConfig] = useState<ConfigState>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFutureClosureConfig();
      const serverConfig = data.config || {};

      const merged: ConfigState = {
        aggression_default: serverConfig.aggression_default ?? DEFAULT_CONFIG.aggression_default,
        base_months_default: serverConfig.base_months_default ?? DEFAULT_CONFIG.base_months_default,
        closure_duration_months: serverConfig.closure_duration_months ?? DEFAULT_CONFIG.closure_duration_months,
        custom_continuations: {
          ...DEFAULT_CONFIG.custom_continuations,
          ...(serverConfig.custom_continuations || serverConfig.continuation_rates || {}),
        },
        custom_one_time_costs: {
          ...DEFAULT_CONFIG.custom_one_time_costs,
          ...(serverConfig.custom_one_time_costs || serverConfig.one_time_cost_defaults || {}),
        },
        multiplier_mode: serverConfig.multiplier_mode ?? DEFAULT_CONFIG.multiplier_mode,
      };

      setConfig(merged);
      setOriginalConfig(merged);
      setIsDefault(data.is_default !== false);
      setLastUpdated(data.last_updated || null);
    } catch (err: any) {
      console.error('Failed to load config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateFutureClosureConfig(config);
      setOriginalConfig(config);
      setIsDefault(false);
      setLastUpdated(new Date().toISOString());
      setSuccess('Configuration saved successfully');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
  };

  const handleRevert = () => {
    setConfig(originalConfig);
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  const updateContinuation = (key: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      custom_continuations: {
        ...prev.custom_continuations,
        [key]: Math.max(0, Math.min(100, value)),
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mr-3" />
        <span className="text-gray-500">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isDefault ? (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
              Using Defaults
            </span>
          ) : (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
              Custom Config
            </span>
          )}
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Last updated: {new Date(lastUpdated).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <button
              onClick={handleRevert}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition-colors"
            >
              Undo Changes
            </button>
          )}
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm text-amber-600 hover:text-amber-700 border border-amber-300 rounded-lg transition-colors flex items-center gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg transition-colors flex items-center gap-1"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Section 1: General Defaults */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-gray-500" />
          Calculation Defaults
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Aggression Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Aggression Level
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={config.aggression_default}
              onChange={e => setConfig(prev => ({ ...prev, aggression_default: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gradient-to-r from-blue-200 via-blue-400 to-red-400 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Conservative (0)</span>
              <span className="font-semibold text-gray-900">{config.aggression_default}</span>
              <span>Aggressive (100)</span>
            </div>
          </div>

          {/* Base Months */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Base Period (months)
            </label>
            <select
              value={config.base_months_default}
              onChange={e => setConfig(prev => ({ ...prev, base_months_default: parseInt(e.target.value) }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {[12, 24, 36, 48, 60, 72, 84, 96].map(m => (
                <option key={m} value={m}>{m} months ({(m / 12).toFixed(0)} yr{m > 12 ? 's' : ''})</option>
              ))}
            </select>
          </div>

          {/* Closure Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Closure Duration (months)
            </label>
            <select
              value={config.closure_duration_months}
              onChange={e => setConfig(prev => ({ ...prev, closure_duration_months: parseInt(e.target.value) }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {[12, 18, 24, 30, 36, 48, 60].map(m => (
                <option key={m} value={m}>{m} months ({(m / 12).toFixed(1)} yrs)</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Expense Continuation Rates */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-gray-500" />
          Default Expense Continuation Rates
        </h3>
        <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Percentage of each expense category that continues during forced closure
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(CONTINUATION_LABELS).map(([key, { label, description }]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{label}</label>
                <span className="text-xs text-gray-400">{description}</span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={config.custom_continuations[key as keyof typeof config.custom_continuations]}
                  onChange={e => updateContinuation(key, parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.custom_continuations[key as keyof typeof config.custom_continuations]}
                    onChange={e => updateContinuation(key, parseInt(e.target.value) || 0)}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3: One-Time Costs */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-gray-500" />
          Default One-Time Cost Multipliers
        </h3>
        <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Percentage of annual revenue applied as one-time shutdown/restart costs
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shutdown Cost (% of annual revenue)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="25"
                step="0.5"
                value={config.custom_one_time_costs.shutdown_pct}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  custom_one_time_costs: { ...prev.custom_one_time_costs, shutdown_pct: parseFloat(e.target.value) }
                }))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="25"
                  step="0.5"
                  value={config.custom_one_time_costs.shutdown_pct}
                  onChange={e => setConfig(prev => ({
                    ...prev,
                    custom_one_time_costs: { ...prev.custom_one_time_costs, shutdown_pct: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Restart Cost (% of annual revenue)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="25"
                step="0.5"
                value={config.custom_one_time_costs.restart_pct}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  custom_one_time_costs: { ...prev.custom_one_time_costs, restart_pct: parseFloat(e.target.value) }
                }))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="25"
                  step="0.5"
                  value={config.custom_one_time_costs.restart_pct}
                  onChange={e => setConfig(prev => ({
                    ...prev,
                    custom_one_time_costs: { ...prev.custom_one_time_costs, restart_pct: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Multiplier Mode */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-gray-500" />
          Multiplier Mode
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MULTIPLIER_MODES.map(mode => (
            <label
              key={mode.value}
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                config.multiplier_mode === mode.value
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="multiplier_mode"
                value={mode.value}
                checked={config.multiplier_mode === mode.value}
                onChange={e => setConfig(prev => ({ ...prev, multiplier_mode: e.target.value }))}
                className="mt-0.5 h-4 w-4 text-blue-600"
              />
              <div>
                <span className="block text-sm font-medium text-gray-900">{mode.label}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{mode.description}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Unsaved changes bar */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-amber-700 font-medium">You have unsaved changes</span>
          <div className="flex gap-2">
            <button
              onClick={handleRevert}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-1"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
